import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileText,
  Plus,
  Download,
  Eye,
  X,
  Loader2,
  CheckCircle,
  Pen,
  Trash2,
  Edit2,
  Save,
  FileSignature,
  Copy,
  RefreshCw,
  Filter,
  Search,
  Building2,
  User,
  Calendar,
  Clock,
  Bookmark,
  Send,
  Calculator,
  DollarSign,
  MapPin,
  AlertTriangle,
  BarChart3,
  TrendingUp,
  Zap,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============ Signature Canvas Component ============
const SignatureCanvas = ({ onSave, onCancel, signerName, onSignerNameChange }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#1a1a2e';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const handleSave = () => {
    if (!hasSignature) {
      toast.error('Please draw your signature');
      return;
    }
    if (!signerName.trim()) {
      toast.error('Please enter your name');
      return;
    }
    const canvas = canvasRef.current;
    const dataUrl = canvas.toDataURL('image/png');
    onSave(dataUrl);
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm text-slate-300 mb-2">Your Full Name</label>
        <input
          type="text"
          value={signerName}
          onChange={(e) => onSignerNameChange(e.target.value)}
          placeholder="Enter your full name"
          className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-neon-cyan/50 focus:outline-none"
        />
      </div>

      <div>
        <label className="block text-sm text-slate-300 mb-2">Draw Your Signature</label>
        <div className="bg-white rounded-lg p-2">
          <canvas
            ref={canvasRef}
            width={400}
            height={150}
            className="w-full border border-slate-200 rounded cursor-crosshair touch-none"
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
          />
        </div>
        <p className="text-xs text-slate-500 mt-1">Draw your signature using mouse or touch</p>
      </div>

      <div className="flex justify-between">
        <button
          onClick={clearCanvas}
          className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors"
        >
          Clear
        </button>
        <div className="flex space-x-2">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm bg-white/10 hover:bg-white/20 rounded-lg text-white"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!hasSignature || !signerName.trim()}
            className="px-4 py-2 text-sm bg-neon-cyan text-slate-900 rounded-lg hover:bg-neon-cyan/80 disabled:opacity-50 flex items-center space-x-2"
          >
            <FileSignature className="w-4 h-4" />
            <span>Apply Signature</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// ============ Template Selector ============
const TemplateSelector = ({ templates, selectedTemplate, onSelect, onClose }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
    onClick={onClose}
  >
    <motion.div
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.9, opacity: 0 }}
      className="bg-slate-800 rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden border border-white/10"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between p-4 border-b border-white/10">
        <h3 className="text-lg font-bold text-white">Select Contract Template</h3>
        <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 text-slate-400">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="p-4 overflow-y-auto max-h-[60vh]">
        <div className="grid gap-4">
          {templates.map(template => (
            <motion.div
              key={template.id}
              whileHover={{ scale: 1.01 }}
              onClick={() => onSelect(template)}
              className={`p-4 rounded-xl border cursor-pointer transition-all ${
                selectedTemplate?.id === template.id
                  ? 'border-neon-cyan bg-neon-cyan/10'
                  : 'border-white/10 bg-white/5 hover:border-white/20'
              }`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="font-semibold text-white">{template.name}</h4>
                  <p className="text-sm text-slate-400 mt-1">{template.description}</p>
                </div>
                <span className={`px-2 py-1 rounded text-xs capitalize ${
                  template.category === 'partnership' ? 'bg-purple-500/20 text-purple-400' :
                  template.category === 'driver' ? 'bg-green-500/20 text-green-400' :
                  template.category === 'client' ? 'bg-blue-500/20 text-blue-400' :
                  'bg-slate-500/20 text-slate-400'
                }`}>
                  {template.category}
                </span>
              </div>
              <div className="flex items-center mt-3 text-xs text-slate-500">
                <FileText className="w-3 h-3 mr-1" />
                {template.fields?.length || 0} fields
                {template.is_default && (
                  <span className="ml-2 px-2 py-0.5 bg-neon-cyan/20 text-neon-cyan rounded">
                    Default
                  </span>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.div>
  </motion.div>
);

// ============ Contract Form ============
const ContractForm = ({ template, onSubmit, onCancel, initialValues = {} }) => {
  const [formValues, setFormValues] = useState(initialValues);
  const [signatures, setSignatures] = useState({});
  const [activeSignatureField, setActiveSignatureField] = useState(null);
  const [signerName, setSignerName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleFieldChange = (fieldName, value) => {
    setFormValues(prev => ({ ...prev, [fieldName]: value }));
  };

  const handleSignatureSave = (dataUrl) => {
    setSignatures(prev => ({
      ...prev,
      [activeSignatureField]: {
        dataUrl,
        name: signerName,
        timestamp: new Date().toISOString()
      }
    }));
    setActiveSignatureField(null);
    setSignerName('');
    toast.success('Signature applied');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    const missingFields = template.fields
      .filter(f => f.required && f.type !== 'signature' && !formValues[f.name])
      .map(f => f.label);
    
    if (missingFields.length > 0) {
      toast.error(`Missing required fields: ${missingFields.join(', ')}`);
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit(formValues, signatures);
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field) => {
    const value = formValues[field.name] || field.default_value || '';

    switch (field.type) {
      case 'signature':
        const sig = signatures[field.name];
        return (
          <div key={field.name} className="col-span-2">
            <label className="block text-sm text-slate-300 mb-2">
              {field.label} {field.required && <span className="text-red-400">*</span>}
            </label>
            {sig ? (
              <div className="flex items-center space-x-4 p-3 bg-white/5 rounded-lg">
                <img src={sig.dataUrl} alt="Signature" className="h-12 rounded" />
                <div>
                  <p className="text-sm text-white">{sig.name}</p>
                  <p className="text-xs text-slate-400">{new Date(sig.timestamp).toLocaleString()}</p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const newSigs = { ...signatures };
                    delete newSigs[field.name];
                    setSignatures(newSigs);
                  }}
                  className="ml-auto text-red-400 hover:text-red-300"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setActiveSignatureField(field.name)}
                className="w-full py-8 border-2 border-dashed border-white/20 rounded-lg hover:border-neon-cyan/50 transition-colors flex flex-col items-center justify-center text-slate-400 hover:text-neon-cyan"
              >
                <Pen className="w-6 h-6 mb-2" />
                <span>Click to sign</span>
              </button>
            )}
          </div>
        );

      case 'textarea':
        return (
          <div key={field.name} className="col-span-2">
            <label className="block text-sm text-slate-300 mb-2">
              {field.label} {field.required && <span className="text-red-400">*</span>}
            </label>
            <textarea
              value={value}
              onChange={(e) => handleFieldChange(field.name, e.target.value)}
              placeholder={field.placeholder || ''}
              rows={3}
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-neon-cyan/50 focus:outline-none resize-none"
            />
          </div>
        );

      case 'date':
        return (
          <div key={field.name}>
            <label className="block text-sm text-slate-300 mb-2">
              {field.label} {field.required && <span className="text-red-400">*</span>}
            </label>
            <input
              type="date"
              value={value}
              onChange={(e) => handleFieldChange(field.name, e.target.value)}
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-neon-cyan/50 focus:outline-none"
            />
          </div>
        );

      case 'number':
        return (
          <div key={field.name}>
            <label className="block text-sm text-slate-300 mb-2">
              {field.label} {field.required && <span className="text-red-400">*</span>}
            </label>
            <input
              type="number"
              value={value}
              onChange={(e) => handleFieldChange(field.name, e.target.value)}
              placeholder={field.placeholder || ''}
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-neon-cyan/50 focus:outline-none"
            />
          </div>
        );

      case 'checkbox':
        return (
          <div key={field.name} className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={value === true || value === 'true'}
              onChange={(e) => handleFieldChange(field.name, e.target.checked)}
              className="w-4 h-4 rounded"
            />
            <label className="text-sm text-slate-300">{field.label}</label>
          </div>
        );

      default:
        return (
          <div key={field.name}>
            <label className="block text-sm text-slate-300 mb-2">
              {field.label} {field.required && <span className="text-red-400">*</span>}
            </label>
            <input
              type="text"
              value={value}
              onChange={(e) => handleFieldChange(field.name, e.target.value)}
              placeholder={field.placeholder || ''}
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-neon-cyan/50 focus:outline-none"
            />
          </div>
        );
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        {template.fields?.map(renderField)}
      </div>

      <div className="flex justify-end space-x-3 pt-4 border-t border-white/10">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="px-4 py-2 bg-neon-cyan text-slate-900 rounded-lg hover:bg-neon-cyan/80 font-medium flex items-center space-x-2"
        >
          {submitting ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <Save className="w-4 h-4" />
              <span>Create Contract</span>
            </>
          )}
        </button>
      </div>

      {/* Signature Modal */}
      <AnimatePresence>
        {activeSignatureField && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-slate-800 rounded-2xl w-full max-w-md p-6 border border-white/10"
            >
              <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                <FileSignature className="w-5 h-5 mr-2 text-neon-cyan" />
                Sign Document
              </h3>
              <SignatureCanvas
                onSave={handleSignatureSave}
                onCancel={() => setActiveSignatureField(null)}
                signerName={signerName}
                onSignerNameChange={setSignerName}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </form>
  );
};

// ============ Main Component ============
export const EnhancedContractBuilder = () => {
  const [activeTab, setActiveTab] = useState('dashboard'); // dashboard, contracts, calculator, zip-config
  const [view, setView] = useState('list'); // list, create, preview
  const [templates, setTemplates] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedContract, setSelectedContract] = useState(null);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [filterCategory, setFilterCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [selectedContractForSign, setSelectedContractForSign] = useState(null);
  const [signerEmail, setSignerEmail] = useState('');
  const [signerName, setSignerName] = useState('');
  const [signerMessage, setSignerMessage] = useState('');
  const [sendingSignature, setSendingSignature] = useState(false);
  
  // Multi-signer state
  const [signers, setSigners] = useState([{ name: '', email: '', role: 'Signer', order: 1 }]);
  const [signingOrder, setSigningOrder] = useState('parallel');
  const [requireInitials, setRequireInitials] = useState(false);
  const [requireWitness, setRequireWitness] = useState(false);
  
  // Dashboard State
  const [onboardingRecords, setOnboardingRecords] = useState([]);
  const [ovaDispatchHealth, setOvaDispatchHealth] = useState(null);
  
  // Pricing Calculator State
  const [calcForm, setCalcForm] = useState({
    zip_code: '77001',
    service_type: 'nemt',
    trip_type: 'one_way',
    estimated_miles: 10,
    estimated_minutes: 30,
    scheduled_time: '',
    wait_time_minutes: 0,
    vehicle_type: 'sedan',
  });
  const [priceBreakdown, setPriceBreakdown] = useState(null);
  const [calculating, setCalculating] = useState(false);
  
  // ZIP Config State
  const [zipConfigs, setZipConfigs] = useState([]);
  const [editingZip, setEditingZip] = useState(null);
  const [showZipModal, setShowZipModal] = useState(false);
  const [zipForm, setZipForm] = useState({
    zip_code: '',
    region: '',
    base_fare: 3.50,
    nemt_base_fare: 8.00,
    per_mile_rate: 1.85,
    per_minute_rate: 0.25,
    minimum_fare: 8.00,
    wait_time_free_minutes: 5,
    wait_time_per_minute: 0.40,
    market_adjustment: 1.0,
  });

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [templatesRes, contractsRes, zipRes, ovaHealthRes, onboardingRes] = await Promise.all([
        fetch(`${API_URL}/api/contract-builder/templates`),
        fetch(`${API_URL}/api/contract-builder/contracts`),
        fetch(`${API_URL}/api/contracts/zip-pricing`),
        fetch(`${API_URL}/api/contract-builder/ova-dispatch/health`),
        fetch(`${API_URL}/api/contract-builder/onboarding`)
      ]);

      if (templatesRes.ok) {
        const data = await templatesRes.json();
        setTemplates(data.templates || []);
      }

      if (contractsRes.ok) {
        const data = await contractsRes.json();
        setContracts(data.contracts || []);
      }
      
      if (zipRes.ok) {
        const data = await zipRes.json();
        setZipConfigs(data || []);
      }
      
      if (ovaHealthRes.ok) {
        const data = await ovaHealthRes.json();
        setOvaDispatchHealth(data);
      }
      
      if (onboardingRes.ok) {
        const data = await onboardingRes.json();
        setOnboardingRecords(data.records || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load contracts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  // Calculate price
  const calculatePrice = async () => {
    if (!calcForm.zip_code) {
      toast.error('Please enter a ZIP code');
      return;
    }
    
    setCalculating(true);
    try {
      const res = await fetch(`${API_URL}/api/contracts/calculate-price`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(calcForm)
      });
      
      if (res.ok) {
        const data = await res.json();
        setPriceBreakdown(data);
      } else {
        toast.error('Failed to calculate price');
      }
    } catch (error) {
      toast.error('Failed to calculate price');
    } finally {
      setCalculating(false);
    }
  };
  
  // Save ZIP config
  const saveZipConfig = async () => {
    if (!zipForm.zip_code) {
      toast.error('ZIP code is required');
      return;
    }
    
    try {
      const method = editingZip ? 'PUT' : 'POST';
      const url = editingZip 
        ? `${API_URL}/api/contracts/zip-pricing/${zipForm.zip_code}`
        : `${API_URL}/api/contracts/zip-pricing`;
        
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(zipForm)
      });
      
      if (res.ok) {
        toast.success(editingZip ? 'ZIP config updated' : 'ZIP config created');
        setShowZipModal(false);
        setEditingZip(null);
        setZipForm({
          zip_code: '',
          region: '',
          base_fare: 3.50,
          nemt_base_fare: 8.00,
          per_mile_rate: 1.85,
          per_minute_rate: 0.25,
          minimum_fare: 8.00,
          wait_time_free_minutes: 5,
          wait_time_per_minute: 0.40,
          market_adjustment: 1.0,
        });
        fetchData();
      } else {
        toast.error('Failed to save ZIP config');
      }
    } catch (error) {
      toast.error('Failed to save ZIP config');
    }
  };
  
  // Delete ZIP config
  const deleteZipConfig = async (zipCode) => {
    if (!confirm(`Delete pricing for ZIP ${zipCode}?`)) return;
    
    try {
      const res = await fetch(`${API_URL}/api/contracts/zip-pricing/${zipCode}`, {
        method: 'DELETE'
      });
      
      if (res.ok) {
        toast.success('ZIP config deleted');
        fetchData();
      } else {
        toast.error('Failed to delete ZIP config');
      }
    } catch (error) {
      toast.error('Failed to delete ZIP config');
    }
  };

  // Create contract
  const handleCreateContract = async (fieldValues, signatures) => {
    try {
      // Create contract
      const res = await fetch(`${API_URL}/api/contract-builder/contracts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          template_id: selectedTemplate.id,
          field_values: fieldValues
        })
      });

      if (!res.ok) throw new Error('Failed to create contract');

      const data = await res.json();
      const contractId = data.contract.id;

      // Add signatures
      for (const [sigType, sigData] of Object.entries(signatures)) {
        await fetch(`${API_URL}/api/contract-builder/contracts/${contractId}/sign`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contract_id: contractId,
            signature_type: sigType,
            signature_data_url: sigData.dataUrl,
            signer_name: sigData.name
          })
        });
      }

      toast.success('Contract created successfully!');
      setView('list');
      setSelectedTemplate(null);
      await fetchData();
    } catch (error) {
      toast.error('Failed to create contract');
    }
  };

  // Download PDF
  const handleDownloadPdf = async (contract) => {
    try {
      const res = await fetch(`${API_URL}/api/contract-builder/contracts/${contract.id}/pdf`);
      if (!res.ok) throw new Error('Failed to download PDF');

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${contract.template_name.replace(/\s+/g, '_')}_${contract.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  // Save as template
  const handleSaveAsTemplate = async (contract) => {
    const name = prompt('Enter template name:', `${contract.template_name} - Copy`);
    if (!name) return;

    try {
      const res = await fetch(`${API_URL}/api/contract-builder/contracts/${contract.id}/save-as-template`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contract_id: contract.id,
          template_name: name
        })
      });

      if (!res.ok) throw new Error('Failed to save template');

      toast.success('Saved as template');
      await fetchData();
    } catch (error) {
      toast.error('Failed to save template');
    }
  };

  // Delete contract
  const handleDeleteContract = async (contract) => {
    if (!confirm(`Delete ${contract.template_name}?`)) return;

    try {
      const res = await fetch(`${API_URL}/api/contract-builder/contracts/${contract.id}`, {
        method: 'DELETE'
      });

      if (!res.ok) throw new Error('Failed to delete');

      toast.success('Contract deleted');
      await fetchData();
    } catch (error) {
      toast.error('Failed to delete contract');
    }
  };

  // Open Send for Signature modal
  const openSignatureModal = (contract) => {
    setSelectedContractForSign(contract);
    // Pre-fill first signer from contract data
    const defaultName = contract.field_values?.partner_company || 
                        contract.field_values?.driver_name || 
                        contract.field_values?.facility_name || 
                        contract.field_values?.client_name || '';
    const defaultEmail = contract.field_values?.email || 
                         contract.field_values?.partner_email || '';
    setSigners([{ name: defaultName, email: defaultEmail, role: 'Client', order: 1 }]);
    setSignerMessage('');
    setSigningOrder('parallel');
    setRequireInitials(false);
    setRequireWitness(false);
    setShowSignatureModal(true);
  };
  
  // Add a new signer
  const addSigner = () => {
    setSigners([...signers, { name: '', email: '', role: 'Signer', order: signers.length + 1 }]);
  };
  
  // Remove a signer
  const removeSigner = (index) => {
    if (signers.length > 1) {
      const updated = signers.filter((_, i) => i !== index).map((s, i) => ({ ...s, order: i + 1 }));
      setSigners(updated);
    }
  };
  
  // Update a signer
  const updateSigner = (index, field, value) => {
    const updated = [...signers];
    updated[index] = { ...updated[index], [field]: value };
    setSigners(updated);
  };

  // Send for Signature (supports single or multi-signer)
  const handleSendForSignature = async () => {
    // Validate all signers
    const validSigners = signers.filter(s => s.email && s.name);
    if (validSigners.length === 0) {
      toast.error('Please add at least one signer with name and email');
      return;
    }

    setSendingSignature(true);
    try {
      // Use multi-signer endpoint (works for single signer too)
      const res = await fetch(`${API_URL}/api/contract-builder/contracts/${selectedContractForSign.id}/send-for-multi-signature`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          signers: validSigners.map((s, idx) => ({
            name: s.name,
            email: s.email,
            role: s.role || 'Signer',
            order: idx + 1
          })),
          signing_order: signingOrder,
          require_initials: requireInitials,
          require_witness: requireWitness,
          message: signerMessage || null
        })
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to send');
      }

      const result = await res.json();
      const signerCount = validSigners.length;
      toast.success(
        signerCount === 1 
          ? `Signature request sent to ${validSigners[0].email}`
          : `Signature requests sent to ${signerCount} signers`
      );
      
      setShowSignatureModal(false);
      setSelectedContractForSign(null);
      setSigners([{ name: '', email: '', role: 'Signer', order: 1 }]);
      setSignerMessage('');
      await fetchData();
    } catch (error) {
      toast.error(error.message || 'Failed to send signature request');
    } finally {
      setSendingSignature(false);
    }
  };

  // Filter contracts
  const filteredContracts = contracts.filter(contract => {
    if (filterCategory && contract.category !== filterCategory) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const values = Object.values(contract.field_values || {}).join(' ').toLowerCase();
      if (!contract.template_name.toLowerCase().includes(q) && !values.includes(q)) return false;
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-neon-cyan animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="contracts-management">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white flex items-center">
            <FileSignature className="w-6 h-6 mr-2 text-neon-cyan" />
            Contracts
          </h1>
          <p className="text-slate-400 text-sm mt-1">Create, sign, and manage contracts with PDF export & pricing</p>
        </div>

        <div className="flex items-center space-x-3">
          <button
            onClick={fetchData}
            className="btn-secondary flex items-center space-x-2"
          >
            <RefreshCw className="w-4 h-4" />
            <span className="hidden sm:inline">Refresh</span>
          </button>
          {activeTab === 'contracts' && (
            <button
              onClick={() => setShowTemplateSelector(true)}
              className="btn-primary flex items-center space-x-2"
            >
              <Plus className="w-4 h-4" />
              <span>New Contract</span>
            </button>
          )}
          {activeTab === 'zip-config' && (
            <button
              onClick={() => { setEditingZip(null); setShowZipModal(true); }}
              className="btn-primary flex items-center space-x-2"
            >
              <Plus className="w-4 h-4" />
              <span>New ZIP Zone</span>
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center space-x-2 border-b border-white/10 pb-4 overflow-x-auto">
        {[
          { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
          { id: 'contracts', label: 'Contracts', icon: FileText },
          { id: 'calculator', label: 'Price Calculator', icon: Calculator },
          { id: 'zip-config', label: 'ZIP Pricing', icon: MapPin }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${
              activeTab === tab.id
                ? 'bg-neon-cyan/20 text-neon-cyan'
                : 'text-slate-400 hover:text-white hover:bg-white/5'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && (
        <div className="space-y-6">
          {/* Key Metrics */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="glass-card p-4 border-l-4 border-neon-cyan">
              <div className="flex items-center justify-between">
                <FileText className="w-5 h-5 text-neon-cyan" />
                <TrendingUp className="w-4 h-4 text-green-400" />
              </div>
              <div className="text-3xl font-bold text-white mt-2">{contracts.length}</div>
              <div className="text-sm text-slate-400">Total Contracts</div>
            </div>
            <div className="glass-card p-4 border-l-4 border-green-400">
              <div className="flex items-center justify-between">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-400 rounded-full">
                  {contracts.length > 0 ? Math.round((contracts.filter(c => c.status === 'signed').length / contracts.length) * 100) : 0}%
                </span>
              </div>
              <div className="text-3xl font-bold text-white mt-2">
                {contracts.filter(c => c.status === 'signed').length}
              </div>
              <div className="text-sm text-slate-400">Signed</div>
            </div>
            <div className="glass-card p-4 border-l-4 border-amber-400">
              <div className="flex items-center justify-between">
                <Clock className="w-5 h-5 text-amber-400" />
                <Send className="w-4 h-4 text-amber-400" />
              </div>
              <div className="text-3xl font-bold text-white mt-2">
                {contracts.filter(c => c.status === 'pending_signature').length}
              </div>
              <div className="text-sm text-slate-400">Awaiting Signature</div>
            </div>
            <div className="glass-card p-4 border-l-4 border-purple-400">
              <div className="flex items-center justify-between">
                <Zap className="w-5 h-5 text-purple-400" />
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  ovaDispatchHealth?.status === 'healthy' 
                    ? 'bg-green-500/20 text-green-400' 
                    : 'bg-amber-500/20 text-amber-400'
                }`}>
                  {ovaDispatchHealth?.status === 'healthy' ? 'Connected' : 'Check Config'}
                </span>
              </div>
              <div className="text-3xl font-bold text-white mt-2">
                {onboardingRecords.filter(r => r.ova_dispatch?.success).length}
              </div>
              <div className="text-sm text-slate-400">OVA Dispatch Synced</div>
            </div>
          </div>

          {/* Charts Row */}
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Contract Status Distribution */}
            <div className="glass-card p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                <BarChart3 className="w-5 h-5 mr-2 text-neon-cyan" />
                Contract Status Distribution
              </h3>
              <div className="space-y-4">
                {[
                  { status: 'signed', label: 'Signed', color: 'bg-green-500', textColor: 'text-green-400' },
                  { status: 'pending_signature', label: 'Pending Signature', color: 'bg-amber-500', textColor: 'text-amber-400' },
                  { status: 'draft', label: 'Draft', color: 'bg-slate-500', textColor: 'text-slate-400' },
                ].map(item => {
                  const count = contracts.filter(c => c.status === item.status).length;
                  const percentage = contracts.length > 0 ? (count / contracts.length) * 100 : 0;
                  return (
                    <div key={item.status}>
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-sm ${item.textColor}`}>{item.label}</span>
                        <span className="text-sm text-white">{count}</span>
                      </div>
                      <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                        <motion.div 
                          initial={{ width: 0 }}
                          animate={{ width: `${percentage}%` }}
                          transition={{ duration: 0.8, ease: "easeOut" }}
                          className={`h-full ${item.color} rounded-full`}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Completion Rate */}
              <div className="mt-6 pt-4 border-t border-white/10">
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Completion Rate</span>
                  <span className="text-2xl font-bold text-neon-green">
                    {contracts.length > 0 
                      ? Math.round((contracts.filter(c => c.status === 'signed').length / contracts.length) * 100) 
                      : 0}%
                  </span>
                </div>
              </div>
            </div>

            {/* OVA Dispatch Integration Status */}
            <div className="glass-card p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                <Zap className="w-5 h-5 mr-2 text-purple-400" />
                OVA Dispatch Integration
              </h3>
              
              {/* Connection Status */}
              <div className={`p-4 rounded-lg mb-4 ${
                ovaDispatchHealth?.status === 'healthy' 
                  ? 'bg-green-500/10 border border-green-500/30' 
                  : 'bg-amber-500/10 border border-amber-500/30'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`w-3 h-3 rounded-full ${
                      ovaDispatchHealth?.status === 'healthy' ? 'bg-green-400 animate-pulse' : 'bg-amber-400'
                    }`} />
                    <div>
                      <div className={ovaDispatchHealth?.status === 'healthy' ? 'text-green-400' : 'text-amber-400'}>
                        {ovaDispatchHealth?.status === 'healthy' ? 'Connected' : ovaDispatchHealth?.status || 'Checking...'}
                      </div>
                      <div className="text-xs text-slate-500">
                        {ovaDispatchHealth?.dispatch_url || 'OVA Dispatch API'}
                      </div>
                    </div>
                  </div>
                  {ovaDispatchHealth?.dispatch_response?.secret_configured && (
                    <CheckCircle className="w-5 h-5 text-green-400" />
                  )}
                </div>
              </div>

              {/* Sync Stats */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-white/5 rounded-lg">
                  <div className="text-2xl font-bold text-white">
                    {onboardingRecords.filter(r => r.ova_dispatch?.success).length}
                  </div>
                  <div className="text-sm text-slate-400">Successfully Synced</div>
                </div>
                <div className="p-4 bg-white/5 rounded-lg">
                  <div className="text-2xl font-bold text-white">
                    {onboardingRecords.filter(r => r.ova_dispatch && !r.ova_dispatch.success).length}
                  </div>
                  <div className="text-sm text-slate-400">Pending/Failed</div>
                </div>
              </div>

              {/* Recent Syncs */}
              {onboardingRecords.length > 0 && (
                <div className="mt-4">
                  <div className="text-sm text-slate-400 mb-2">Recent Onboardings</div>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {onboardingRecords.slice(0, 5).map((record, idx) => (
                      <div key={record.id || idx} className="flex items-center justify-between py-2 border-b border-white/5">
                        <div className="flex items-center space-x-2">
                          <div className={`w-2 h-2 rounded-full ${
                            record.ova_dispatch?.success ? 'bg-green-400' : 'bg-amber-400'
                          }`} />
                          <span className="text-sm text-white truncate max-w-[150px]">
                            {record.client_name || 'Unknown'}
                          </span>
                        </div>
                        {record.ova_dispatch?.org_id && (
                          <span className="text-xs text-slate-500">{record.ova_dispatch.org_id}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <Clock className="w-5 h-5 mr-2 text-neon-cyan" />
              Recent Contract Activity
            </h3>
            {contracts.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No contracts yet. Create your first contract to get started.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-sm text-slate-400 border-b border-white/10">
                      <th className="pb-3 font-medium">Contract</th>
                      <th className="pb-3 font-medium">Category</th>
                      <th className="pb-3 font-medium">Status</th>
                      <th className="pb-3 font-medium">Created</th>
                      <th className="pb-3 font-medium">OVA Sync</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contracts.slice(0, 5).map(contract => (
                      <tr key={contract.id} className="border-b border-white/5 hover:bg-white/5">
                        <td className="py-3">
                          <div className="font-medium text-white">{contract.name}</div>
                        </td>
                        <td className="py-3">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            contract.category === 'partnership' ? 'bg-purple-500/20 text-purple-400' :
                            contract.category === 'driver' ? 'bg-green-500/20 text-green-400' :
                            contract.category === 'client' ? 'bg-blue-500/20 text-blue-400' :
                            'bg-slate-500/20 text-slate-400'
                          }`}>
                            {contract.category || 'General'}
                          </span>
                        </td>
                        <td className="py-3">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            contract.status === 'signed' ? 'bg-green-500/20 text-green-400' :
                            contract.status === 'pending_signature' ? 'bg-amber-500/20 text-amber-400' :
                            'bg-slate-500/20 text-slate-400'
                          }`}>
                            {contract.status?.replace('_', ' ') || 'Draft'}
                          </span>
                        </td>
                        <td className="py-3 text-sm text-slate-400">
                          {contract.created_at ? new Date(contract.created_at).toLocaleDateString() : '-'}
                        </td>
                        <td className="py-3">
                          {contract.ova_dispatch_org_id ? (
                            <a
                              href={contract.ova_dispatch_portal_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center space-x-1 text-neon-cyan hover:text-neon-cyan/80"
                            >
                              <CheckCircle className="w-4 h-4" />
                              <span className="text-xs">{contract.ova_dispatch_org_id}</span>
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          ) : (
                            <span className="text-slate-500 text-xs">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Contracts Tab */}
      {activeTab === 'contracts' && (
        <>
          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="glass-card p-4">
              <div className="flex items-center justify-between">
                <FileText className="w-5 h-5 text-neon-cyan" />
                <span className="text-xs text-slate-400">Total</span>
              </div>
              <div className="text-2xl font-bold text-white mt-2">{contracts.length}</div>
              <div className="text-xs text-slate-400">Contracts</div>
            </div>
            <div className="glass-card p-4">
              <div className="flex items-center justify-between">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span className="text-xs text-slate-400">Completed</span>
              </div>
              <div className="text-2xl font-bold text-white mt-2">
                {contracts.filter(c => c.status === 'signed').length}
              </div>
              <div className="text-xs text-slate-400">Signed</div>
            </div>
            <div className="glass-card p-4">
              <div className="flex items-center justify-between">
                <Clock className="w-5 h-5 text-amber-400" />
                <span className="text-xs text-slate-400">Pending</span>
              </div>
              <div className="text-2xl font-bold text-white mt-2">
                {contracts.filter(c => c.status === 'pending_signature' || c.status === 'draft').length}
              </div>
              <div className="text-xs text-slate-400">Awaiting Signature</div>
            </div>
            <div className="glass-card p-4">
              <div className="flex items-center justify-between">
                <Bookmark className="w-5 h-5 text-purple-400" />
                <span className="text-xs text-slate-400">Available</span>
              </div>
              <div className="text-2xl font-bold text-white mt-2">{templates.length}</div>
              <div className="text-xs text-slate-400">Templates</div>
            </div>
          </div>

          {/* Filters */}
          <div className="flex items-center space-x-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search contracts..."
            className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-neon-cyan/50 focus:outline-none"
          />
        </div>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-neon-cyan/50 focus:outline-none"
        >
          <option value="">All Categories</option>
          <option value="partnership">Partnership</option>
          <option value="driver">Driver</option>
          <option value="client">Client</option>
          <option value="custom">Custom</option>
        </select>
      </div>

      {/* Contract List */}
      {view === 'list' && (
        <div className="space-y-4">
          {filteredContracts.length === 0 ? (
            <div className="glass-card p-12 text-center">
              <FileText className="w-12 h-12 mx-auto mb-4 text-slate-500 opacity-50" />
              <p className="text-slate-400 mb-4">No contracts yet</p>
              <button
                onClick={() => setShowTemplateSelector(true)}
                className="text-neon-cyan hover:text-neon-cyan/80"
              >
                Create your first contract →
              </button>
            </div>
          ) : (
            <div className="grid gap-4">
              {filteredContracts.map(contract => (
                <motion.div
                  key={contract.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="glass-card p-4 hover:border-neon-cyan/30 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <h3 className="font-semibold text-white">{contract.template_name}</h3>
                        <span className={`px-2 py-0.5 rounded text-xs capitalize ${
                          contract.status === 'signed' ? 'bg-green-500/20 text-green-400' :
                          contract.status === 'pending_signature' ? 'bg-amber-500/20 text-amber-400' :
                          contract.status === 'draft' ? 'bg-slate-500/20 text-slate-400' :
                          'bg-red-500/20 text-red-400'
                        }`}>
                          {contract.status.replace('_', ' ')}
                        </span>
                      </div>
                      
                      <div className="flex items-center space-x-4 mt-2 text-sm text-slate-400">
                        <span className="flex items-center">
                          <Building2 className="w-3 h-3 mr-1" />
                          {contract.field_values?.partner_company || 
                           contract.field_values?.driver_name || 
                           contract.field_values?.facility_name || 
                           'N/A'}
                        </span>
                        <span className="flex items-center">
                          <Calendar className="w-3 h-3 mr-1" />
                          {new Date(contract.created_at).toLocaleDateString()}
                        </span>
                        {contract.signatures?.length > 0 && (
                          <span className="flex items-center text-green-400">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            {contract.signatures.length} signature(s)
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      {contract.status !== 'signed' && (
                        <button
                          onClick={() => openSignatureModal(contract)}
                          className="px-3 py-1.5 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 text-sm flex items-center space-x-1 transition-colors"
                          title="Send for Signature"
                        >
                          <Send className="w-3 h-3" />
                          <span>Send for Signature</span>
                        </button>
                      )}
                      <button
                        onClick={() => handleDownloadPdf(contract)}
                        className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-400 hover:text-neon-cyan transition-colors"
                        title="Download PDF"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleSaveAsTemplate(contract)}
                        className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-400 hover:text-neon-purple transition-colors"
                        title="Save as Template"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteContract(contract)}
                        className="p-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Create View */}
      {view === 'create' && selectedTemplate && (
        <div className="glass-card p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-white">{selectedTemplate.name}</h2>
              <p className="text-sm text-slate-400">{selectedTemplate.description}</p>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs capitalize ${
              selectedTemplate.category === 'partnership' ? 'bg-purple-500/20 text-purple-400' :
              selectedTemplate.category === 'driver' ? 'bg-green-500/20 text-green-400' :
              selectedTemplate.category === 'client' ? 'bg-blue-500/20 text-blue-400' :
              'bg-slate-500/20 text-slate-400'
            }`}>
              {selectedTemplate.category}
            </span>
          </div>
          
          <ContractForm
            template={selectedTemplate}
            onSubmit={handleCreateContract}
            onCancel={() => {
              setView('list');
              setSelectedTemplate(null);
            }}
          />
        </div>
      )}
        </>
      )}

      {/* Price Calculator Tab */}
      {activeTab === 'calculator' && (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Calculator Form */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
              <Calculator className="w-5 h-5 text-neon-cyan" />
              <span>Trip Price Calculator</span>
            </h3>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-slate-300 mb-1">ZIP Code *</label>
                  <input
                    type="text"
                    value={calcForm.zip_code}
                    onChange={(e) => setCalcForm({ ...calcForm, zip_code: e.target.value })}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-neon-cyan/50 focus:outline-none"
                    placeholder="77001"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-300 mb-1">Service Type</label>
                  <select
                    value={calcForm.service_type}
                    onChange={(e) => setCalcForm({ ...calcForm, service_type: e.target.value })}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-neon-cyan/50 focus:outline-none"
                  >
                    <option value="rideshare">Rideshare (MedexOVA)</option>
                    <option value="nemt">NEMT</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-slate-300 mb-1">Trip Type</label>
                  <select
                    value={calcForm.trip_type}
                    onChange={(e) => setCalcForm({ ...calcForm, trip_type: e.target.value })}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-neon-cyan/50 focus:outline-none"
                  >
                    <option value="one_way">One Way</option>
                    <option value="round_trip">Round Trip</option>
                    <option value="hourly">Hourly</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-slate-300 mb-1">Vehicle Type</label>
                  <select
                    value={calcForm.vehicle_type}
                    onChange={(e) => setCalcForm({ ...calcForm, vehicle_type: e.target.value })}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-neon-cyan/50 focus:outline-none"
                  >
                    <option value="sedan">Sedan</option>
                    <option value="suv">SUV (+$25)</option>
                    <option value="wheelchair_van">Wheelchair Van (+$50)</option>
                    <option value="stretcher">Stretcher (+$100)</option>
                    <option value="bariatric">Bariatric (+$75)</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-slate-300 mb-1">Est. Miles</label>
                  <input
                    type="number"
                    value={calcForm.estimated_miles}
                    onChange={(e) => setCalcForm({ ...calcForm, estimated_miles: parseFloat(e.target.value) || 0 })}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-neon-cyan/50 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-300 mb-1">Est. Minutes</label>
                  <input
                    type="number"
                    value={calcForm.estimated_minutes}
                    onChange={(e) => setCalcForm({ ...calcForm, estimated_minutes: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-neon-cyan/50 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-300 mb-1">Wait Time (min)</label>
                  <input
                    type="number"
                    value={calcForm.wait_time_minutes}
                    onChange={(e) => setCalcForm({ ...calcForm, wait_time_minutes: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-neon-cyan/50 focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-slate-300 mb-1">Scheduled Time (for surge pricing)</label>
                <input
                  type="datetime-local"
                  value={calcForm.scheduled_time}
                  onChange={(e) => setCalcForm({ ...calcForm, scheduled_time: e.target.value })}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-neon-cyan/50 focus:outline-none"
                />
              </div>

              <button
                onClick={calculatePrice}
                disabled={calculating}
                className="w-full py-3 bg-gradient-to-r from-neon-cyan to-neon-purple rounded-lg font-semibold text-white hover:opacity-90 disabled:opacity-50 flex items-center justify-center space-x-2"
              >
                {calculating ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Calculator className="w-5 h-5" />
                    <span>Calculate Price</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Price Breakdown */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
              <DollarSign className="w-5 h-5 text-neon-green" />
              <span>Price Breakdown</span>
            </h3>
            
            {priceBreakdown ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  {[
                    { label: 'Base Fare', value: priceBreakdown.base_rate },
                    { label: `Mileage (${calcForm.estimated_miles} mi)`, value: priceBreakdown.mileage_charge },
                    { label: `Time (${calcForm.estimated_minutes} min)`, value: priceBreakdown.time_charge },
                    { label: 'Wait Time', value: priceBreakdown.wait_time_charge, show: priceBreakdown.wait_time_charge > 0 },
                    { label: `Surge (${priceBreakdown.surge_multiplier}x)`, value: priceBreakdown.surge_charge, show: priceBreakdown.surge_charge > 0 },
                    { label: 'Vehicle Type', value: priceBreakdown.vehicle_surcharge, show: priceBreakdown.vehicle_surcharge > 0 },
                    { label: 'Booking Fee', value: 2.00 },
                  ].filter(item => item.show !== false).map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between py-2 border-b border-white/5">
                      <span className="text-slate-400">{item.label}</span>
                      <span className="text-white">${(item.value || 0).toFixed(2)}</span>
                    </div>
                  ))}
                </div>

                <div className="pt-4 border-t border-white/10">
                  <div className="flex items-center justify-between text-lg">
                    <span className="font-semibold text-white">Total</span>
                    <span className="font-bold text-neon-green text-2xl">${priceBreakdown.total.toFixed(2)}</span>
                  </div>
                  {calcForm.trip_type === 'round_trip' && (
                    <p className="text-xs text-slate-500 mt-1">Includes round trip (1.85x one-way)</p>
                  )}
                </div>

                <div className={`p-4 rounded-lg ${priceBreakdown.is_competitive ? 'bg-green-500/10 border border-green-500/30' : 'bg-amber-500/10 border border-amber-500/30'}`}>
                  <div className="flex items-center space-x-2 mb-2">
                    {priceBreakdown.is_competitive ? (
                      <CheckCircle className="w-5 h-5 text-green-400" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-amber-400" />
                    )}
                    <span className={priceBreakdown.is_competitive ? 'text-green-400' : 'text-amber-400'}>
                      {priceBreakdown.is_competitive ? 'Competitive Pricing!' : 'Slightly Above Market'}
                    </span>
                  </div>
                  <div className="text-sm text-slate-400">
                    Uber/Lyft Estimate: ${priceBreakdown.competitor_estimate?.toFixed(2) || 'N/A'}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-slate-500">
                <Calculator className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Enter trip details and calculate to see price breakdown</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ZIP Pricing Config Tab */}
      {activeTab === 'zip-config' && (
        <div className="space-y-6">
          {/* ZIP Config Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="glass-card p-4">
              <div className="flex items-center justify-between">
                <MapPin className="w-5 h-5 text-neon-cyan" />
                <span className="text-xs text-slate-400">Total</span>
              </div>
              <div className="text-2xl font-bold text-white mt-2">{zipConfigs.length}</div>
              <div className="text-xs text-slate-400">ZIP Zones</div>
            </div>
            <div className="glass-card p-4">
              <div className="flex items-center justify-between">
                <DollarSign className="w-5 h-5 text-neon-green" />
                <span className="text-xs text-slate-400">Avg</span>
              </div>
              <div className="text-2xl font-bold text-white mt-2">
                ${zipConfigs.length > 0 
                  ? (zipConfigs.reduce((sum, z) => sum + (z.per_mile_rate || 1.85), 0) / zipConfigs.length).toFixed(2)
                  : '1.85'}
              </div>
              <div className="text-xs text-slate-400">Per Mile Rate</div>
            </div>
          </div>

          {/* ZIP Config List */}
          {zipConfigs.length === 0 ? (
            <div className="glass-card p-12 text-center">
              <MapPin className="w-12 h-12 mx-auto mb-4 text-slate-500 opacity-50" />
              <p className="text-slate-400 mb-4">No ZIP pricing zones configured</p>
              <button
                onClick={() => { setEditingZip(null); setShowZipModal(true); }}
                className="text-neon-cyan hover:text-neon-cyan/80"
              >
                Create your first ZIP zone →
              </button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {zipConfigs.map(config => (
                <div key={config.zip_code} className="glass-card p-4 hover:border-neon-cyan/30 transition-colors">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 rounded-lg bg-neon-cyan/20">
                        <MapPin className="w-4 h-4 text-neon-cyan" />
                      </div>
                      <div>
                        <div className="font-semibold text-white">{config.zip_code}</div>
                        <div className="text-xs text-slate-400">{config.region || 'Default Region'}</div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => {
                          setEditingZip(config);
                          setZipForm(config);
                          setShowZipModal(true);
                        }}
                        className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteZipConfig(config.zip_code)}
                        className="p-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-slate-400">Base Rate:</span>
                      <span className="text-white ml-2">${config.base_fare || 3.50}</span>
                    </div>
                    <div>
                      <span className="text-slate-400">Per Mile:</span>
                      <span className="text-white ml-2">${config.per_mile_rate || 1.85}</span>
                    </div>
                    <div>
                      <span className="text-slate-400">Per Min:</span>
                      <span className="text-white ml-2">${config.per_minute_rate || 0.25}</span>
                    </div>
                    <div>
                      <span className="text-slate-400">Min Fare:</span>
                      <span className="text-white ml-2">${config.minimum_fare || 8.00}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ZIP Config Modal */}
      <AnimatePresence>
        {showZipModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
            onClick={() => setShowZipModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="glass-card p-6 max-w-lg w-full max-h-[80vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-white flex items-center">
                  <MapPin className="w-5 h-5 mr-2 text-neon-cyan" />
                  {editingZip ? 'Edit ZIP Zone' : 'New ZIP Zone'}
                </h3>
                <button onClick={() => setShowZipModal(false)} className="text-slate-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-slate-300 mb-1">ZIP Code *</label>
                    <input
                      type="text"
                      value={zipForm.zip_code}
                      onChange={(e) => setZipForm({ ...zipForm, zip_code: e.target.value })}
                      disabled={!!editingZip}
                      className="w-full input-dark disabled:opacity-50"
                      placeholder="77001"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-300 mb-1">Region Name</label>
                    <input
                      type="text"
                      value={zipForm.region}
                      onChange={(e) => setZipForm({ ...zipForm, region: e.target.value })}
                      className="w-full input-dark"
                      placeholder="Houston Downtown"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-slate-300 mb-1">Base Fare ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={zipForm.base_fare}
                      onChange={(e) => setZipForm({ ...zipForm, base_fare: parseFloat(e.target.value) || 0 })}
                      className="w-full input-dark"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-300 mb-1">NEMT Base Fare ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={zipForm.nemt_base_fare}
                      onChange={(e) => setZipForm({ ...zipForm, nemt_base_fare: parseFloat(e.target.value) || 0 })}
                      className="w-full input-dark"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-slate-300 mb-1">Per Mile Rate ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={zipForm.per_mile_rate}
                      onChange={(e) => setZipForm({ ...zipForm, per_mile_rate: parseFloat(e.target.value) || 0 })}
                      className="w-full input-dark"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-300 mb-1">Per Minute Rate ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={zipForm.per_minute_rate}
                      onChange={(e) => setZipForm({ ...zipForm, per_minute_rate: parseFloat(e.target.value) || 0 })}
                      className="w-full input-dark"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-slate-300 mb-1">Minimum Fare ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={zipForm.minimum_fare}
                      onChange={(e) => setZipForm({ ...zipForm, minimum_fare: parseFloat(e.target.value) || 0 })}
                      className="w-full input-dark"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-300 mb-1">Market Adjustment</label>
                    <input
                      type="number"
                      step="0.01"
                      value={zipForm.market_adjustment}
                      onChange={(e) => setZipForm({ ...zipForm, market_adjustment: parseFloat(e.target.value) || 1.0 })}
                      className="w-full input-dark"
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t border-white/10">
                  <button
                    onClick={() => setShowZipModal(false)}
                    className="px-4 py-2 text-slate-400 hover:text-white"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveZipConfig}
                    className="px-4 py-2 bg-neon-cyan text-slate-900 rounded-lg hover:bg-neon-cyan/80 flex items-center space-x-2"
                  >
                    <Save className="w-4 h-4" />
                    <span>{editingZip ? 'Update' : 'Create'}</span>
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Template Selector Modal */}
      <AnimatePresence>
        {showTemplateSelector && (
          <TemplateSelector
            templates={templates}
            selectedTemplate={selectedTemplate}
            onSelect={(template) => {
              setSelectedTemplate(template);
              setShowTemplateSelector(false);
              setView('create');
            }}
            onClose={() => setShowTemplateSelector(false)}
          />
        )}
      </AnimatePresence>

      {/* Send for Signature Modal (Multi-Signer) */}
      <AnimatePresence>
        {showSignatureModal && selectedContractForSign && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto"
            onClick={() => setShowSignatureModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="glass-card p-6 max-w-2xl w-full my-8 max-h-[90vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-white flex items-center">
                  <Send className="w-5 h-5 mr-2 text-amber-400" />
                  Send for Signature
                </h3>
                <button
                  onClick={() => setShowSignatureModal(false)}
                  className="text-slate-400 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <p className="text-slate-400 text-sm mb-4">
                Send <strong className="text-white">{selectedContractForSign.template_name}</strong> to be signed electronically.
              </p>

              {/* Signers Section */}
              <div className="space-y-4 mb-6">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-white">Signers</label>
                  <button
                    onClick={addSigner}
                    className="text-sm text-neon-cyan hover:text-neon-cyan/80 flex items-center space-x-1"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add Signer</span>
                  </button>
                </div>

                {signers.map((signer, index) => (
                  <div key={index} className="p-4 bg-white/5 rounded-lg border border-white/10 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-400 flex items-center">
                        <User className="w-4 h-4 mr-1" />
                        Signer {index + 1}
                      </span>
                      {signers.length > 1 && (
                        <button
                          onClick={() => removeSigner(index)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">Name *</label>
                        <input
                          type="text"
                          value={signer.name}
                          onChange={(e) => updateSigner(index, 'name', e.target.value)}
                          placeholder="John Doe"
                          className="w-full input-dark text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">Email *</label>
                        <input
                          type="email"
                          value={signer.email}
                          onChange={(e) => updateSigner(index, 'email', e.target.value)}
                          placeholder="john@company.com"
                          className="w-full input-dark text-sm"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Role</label>
                      <select
                        value={signer.role}
                        onChange={(e) => updateSigner(index, 'role', e.target.value)}
                        className="w-full input-dark text-sm"
                      >
                        <option value="Client">Client</option>
                        <option value="Company Representative">Company Representative</option>
                        <option value="Partner">Partner</option>
                        <option value="Driver">Driver</option>
                        <option value="Witness">Witness</option>
                        <option value="Notary">Notary</option>
                        <option value="Signer">Signer</option>
                      </select>
                    </div>
                  </div>
                ))}
              </div>

              {/* Options Section */}
              <div className="space-y-4 mb-6 p-4 bg-white/5 rounded-lg border border-white/10">
                <label className="text-sm font-medium text-white">Options</label>
                
                <div className="space-y-3">
                  {/* Signing Order */}
                  <div>
                    <label className="block text-xs text-slate-400 mb-2">Should signers sign in a specific order?</label>
                    <div className="flex space-x-4">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="signingOrder"
                          checked={signingOrder === 'parallel'}
                          onChange={() => setSigningOrder('parallel')}
                          className="text-neon-cyan focus:ring-neon-cyan"
                        />
                        <span className="text-sm text-slate-300">No (all at once)</span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="signingOrder"
                          checked={signingOrder === 'sequential'}
                          onChange={() => setSigningOrder('sequential')}
                          className="text-neon-cyan focus:ring-neon-cyan"
                        />
                        <span className="text-sm text-slate-300">Yes (in order)</span>
                      </label>
                    </div>
                  </div>

                  {/* Require Initials */}
                  <div>
                    <label className="block text-xs text-slate-400 mb-2">Require initials in addition to signature?</label>
                    <div className="flex space-x-4">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="requireInitials"
                          checked={!requireInitials}
                          onChange={() => setRequireInitials(false)}
                          className="text-neon-cyan focus:ring-neon-cyan"
                        />
                        <span className="text-sm text-slate-300">No</span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="requireInitials"
                          checked={requireInitials}
                          onChange={() => setRequireInitials(true)}
                          className="text-neon-cyan focus:ring-neon-cyan"
                        />
                        <span className="text-sm text-slate-300">Yes</span>
                      </label>
                    </div>
                  </div>

                  {/* Require Witness */}
                  <div>
                    <label className="block text-xs text-slate-400 mb-2">Require witness/notary signature?</label>
                    <div className="flex space-x-4">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="requireWitness"
                          checked={!requireWitness}
                          onChange={() => setRequireWitness(false)}
                          className="text-neon-cyan focus:ring-neon-cyan"
                        />
                        <span className="text-sm text-slate-300">No</span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="requireWitness"
                          checked={requireWitness}
                          onChange={() => setRequireWitness(true)}
                          className="text-neon-cyan focus:ring-neon-cyan"
                        />
                        <span className="text-sm text-slate-300">Yes</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Message */}
              <div className="mb-4">
                <label className="block text-sm text-slate-400 mb-1">Message to signers (optional)</label>
                <textarea
                  value={signerMessage}
                  onChange={(e) => setSignerMessage(e.target.value)}
                  placeholder="Please review and sign this agreement..."
                  rows={2}
                  className="w-full input-dark resize-none"
                />
              </div>

              {/* Info Box */}
              <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/30 mb-4">
                <p className="text-sm text-amber-400">
                  {signers.length === 1 
                    ? 'An email will be sent to the signer with a secure link. The link expires in 7 days.'
                    : signingOrder === 'sequential'
                      ? `Emails will be sent one at a time, starting with Signer 1. Each signer must complete before the next receives their link.`
                      : `All ${signers.length} signers will receive their signing links simultaneously.`
                  }
                </p>
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-3 pt-2 border-t border-white/10">
                <button
                  onClick={() => setShowSignatureModal(false)}
                  className="px-4 py-2 text-slate-400 hover:text-white"
                  disabled={sendingSignature}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendForSignature}
                  disabled={sendingSignature || signers.filter(s => s.email && s.name).length === 0}
                  className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sendingSignature ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      <span>Send {signers.filter(s => s.email && s.name).length > 1 ? `to ${signers.filter(s => s.email && s.name).length} Signers` : 'Request'}</span>
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default EnhancedContractBuilder;
